import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PhotoUpload } from "@/components/PhotoUpload";
import { toast } from "sonner";
import { t as tr } from "@/lib/i18n";

const sel = "w-full h-11 rounded-xl border border-[#E5DEC9] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A2B]/30";
const empty = { name: "", gender: "", birth_year: "", death_year: "", birth_date: "", wedding_date: "", education: "", occupation: "", bio: "", photo_url: "", relative_id: "", relation: "child", other_parent_id: "" };

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#52635B]">{tr(label)}</Label>{children}</div>
);

export const PersonForm = ({ persons, t, isSelf, defaults = {}, onDone, submitLabel = "Submit for approval", allowNoRelative }) => {
  const [f, setF] = useState({ ...empty, ...defaults });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const relSpouses = f.relative_id ? t.spousesOf(f.relative_id) : [];
  const who = isSelf ? "I am" : "This person is";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const toInt = (v) => (v ? parseInt(v, 10) : null);
    const body = { ...f, gender: f.gender || null, birth_year: toInt(f.birth_year), death_year: toInt(f.death_year),
      birth_date: f.birth_date || null, wedding_date: f.wedding_date || null,
      relative_id: f.relative_id || null, relation: f.relative_id ? f.relation : null,
      other_parent_id: f.relation === "child" ? f.other_parent_id || null : null, is_self: isSelf };
    try {
      const { data } = await api.post("/persons", body);
      toast.success(data.status === "approved" ? "Added to the tree" : "Submitted — awaiting 3 family approvals");
      setF({ ...empty, ...defaults });
      onDone?.(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="person-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name *"><Input required value={f.name} onChange={set("name")} className="h-11 rounded-xl" data-testid="form-name-input" /></Field>
        <Field label="Gender">
          <select value={f.gender} onChange={set("gender")} className={sel} data-testid="form-gender-select">
            <option value="">{tr("Prefer not to say")}</option><option value="male">{tr("Male")}</option><option value="female">{tr("Female")}</option><option value="other">{tr("Other")}</option>
          </select>
        </Field>
        <Field label="Birth date"><Input type="date" value={f.birth_date} onChange={set("birth_date")} className="h-11 rounded-xl" data-testid="form-birth-date-input" /></Field>
        <Field label="Birth year (if date unknown)"><Input type="number" value={f.birth_year} onChange={set("birth_year")} disabled={!!f.birth_date} className="h-11 rounded-xl" data-testid="form-birth-year-input" /></Field>
        <Field label="Wedding date"><Input type="date" value={f.wedding_date} onChange={set("wedding_date")} className="h-11 rounded-xl" data-testid="form-wedding-date-input" /></Field>
        <Field label="Education"><Input value={f.education} onChange={set("education")} className="h-11 rounded-xl" data-testid="form-education-input" /></Field>
        <Field label="Current occupation"><Input value={f.occupation} onChange={set("occupation")} className="h-11 rounded-xl" data-testid="form-occupation-input" /></Field>
        <Field label="Year of passing (if any)"><Input type="number" value={f.death_year} onChange={set("death_year")} className="h-11 rounded-xl" data-testid="form-death-year-input" /></Field>
      </div>
      <Field label="Photo"><PhotoUpload value={f.photo_url} name={f.name} onChange={(url) => setF((o) => ({ ...o, photo_url: url }))} testid="form-photo" /></Field>
      <Field label="A few words (story, village, profession…)"><Textarea value={f.bio} onChange={set("bio")} className="rounded-xl" data-testid="form-bio-input" /></Field>

      <div className="rounded-2xl bg-[#F4EDE2] p-5 space-y-4">
        <p className="font-serif text-lg text-[#1E3A2B]">{tr("Place in the family")}</p>
        <div className="grid sm:grid-cols-[260px_1fr] gap-3 items-center">
          <select value={f.relation} onChange={set("relation")} className={sel} data-testid="form-relation-select">
            <option value="child">{tr(`${who} the child of`)}</option>
            <option value="parent">{tr(`${who} the parent of`)}</option>
            <option value="spouse">{tr(`${who} the spouse of`)}</option>
            <option value="elder_sibling">{tr(`${who} the elder brother / sister of`)}</option>
            <option value="younger_sibling">{tr(`${who} the younger brother / sister of`)}</option>
          </select>
          <select value={f.relative_id} onChange={(e) => setF({ ...f, relative_id: e.target.value, other_parent_id: "" })} className={sel} data-testid="form-relative-select" required={!allowNoRelative}>
            <option value="">{allowNoRelative ? "— No relative (first root) —" : tr("Choose a family member")}</option>
            {[...persons].sort((a, b) => a.name.localeCompare(b.name)).map((p) => (
              <option key={p.person_id} value={p.person_id}>{p.name}{p.birth_year ? ` (${p.birth_year})` : ""}</option>
            ))}
          </select>
        </div>
        {f.relation === "child" && relSpouses.length > 0 && (
          <Field label="Other parent">
            <select value={f.other_parent_id} onChange={set("other_parent_id")} className={sel} data-testid="form-other-parent-select">
              <option value="">{tr("Not listed")}</option>
              {relSpouses.map((s) => <option key={s.person_id} value={s.person_id}>{s.name}</option>)}
            </select>
          </Field>
        )}
      </div>
      <Button type="submit" disabled={busy} className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20] h-12 px-8" data-testid="form-submit-btn">
        {busy ? "…" : tr(submitLabel)}
      </Button>
    </form>
  );
};
